import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {usePaymentSettings} from 'hooks/usePaymentSettings';
import {Link} from 'react-router-dom';
import {cn, DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {PATHS} from 'routes/constants';
import {usePath} from 'hooks';
import {useReservation} from 'context/reservation';
import {useFetchEsims} from 'hooks/esim/useFetchEsims';
import {usePostHogTrack, POSTHOG_EVENTS} from 'analytics';
import bigBgImage from './assets/bg-desktop-banner.jpg?no-inline';
import bgImage from './assets/bg-mobile-banner.webp?no-inline';
import airaloLogo from './assets/airalo-logo.png?no-inline';

export function AiraloBanner() {
  const {t} = useTranslation();
  const {currencySymbol} = usePaymentSettings();
  const {isMatch: isMobileMode} = useScreenResize(DEVICE.laptop);
  const {getFullPath} = usePath();
  const {data: reservation} = useReservation();
  const {trackEvent} = usePostHogTrack();
  const {
    data: esims,
    isFetched,
    isFetching,
  } = useFetchEsims({reservation_id: reservation?.id || ''});

  const backgroundImage = useMemo(() => {
    const image = isMobileMode ? bgImage : bigBgImage;
    const degs = isMobileMode ? '90deg' : '0deg';
    return `
      linear-gradient(
        ${degs},
        rgba(255,255,255,0.6) 0%,
        rgba(255,255,255,0.8) 100%
      ),
      url(${image})
    `;
  }, [isMobileMode]);

  return (
    <Link
      className="w-full"
      to={getFullPath(PATHS.esim.manage)}
      onClick={() =>
        trackEvent(POSTHOG_EVENTS.PARTNER_CLICKED, {
          partner: 'airalo',
          partner_category: 'esim',
          component: 'banner',
          has_esims: Boolean(esims?.length),
        })
      }
    >
      <div
        className="flex justify-center items-center self-stretch w-full bg-cover bg-no-repeat bg-[position:0_30%]
        p-2 lg:px-12 lg:bg-[position:0_50%]"
        style={{
          backgroundImage: backgroundImage,
        }}
      >
        <div className="flex flex-col justify-center items-start">
          <div className="flex items-center gap-3">
            <img
              src={airaloLogo}
              alt="airalo-logo"
              height="19"
              width="100"
              className="object-contain w-14 h-14"
            />
            <div
              className={cn(
                'text-sm font-semibold leading-4',
                'inline-flex w-fit self-auto flex items-center',
                'justify-center text-black pointer',
              )}
            >
              {t('airalo_banner.travel_peacefully')}
            </div>

            <div
              className={cn(
                'text-xs border-l border-black pl-4 transition-opacity duration-300',
                {
                  'opacity-0': isFetching || !isFetched,
                },
              )}
            >
              {esims?.length
                ? t('airalo_banner.view_my_esims')
                : t('airalo_banner.starting_price', {currencySymbol})}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
